import { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { setTokens } from "../store/features/pbxSlice.js";

const TWO_MIN = 2 * 60 * 1000;
const LOG = true;
const dbg = (...a) => LOG && console.log(...a);
const emit = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));

const useRefreshPBXToken = () => {
  const dispatch = useDispatch();
  const pbx = useSelector((state) => state.pbx.value);

  const refreshPBXToken = async () => {
    try {
      let api = pbx.domain + "/openapi/v1.0/refresh_token";
      let body = { refresh_token: pbx.refreshToken };

      if (pbx.model === "S-Series") {
        api = pbx.domain + "/api/v2.0.0/heartbeat?token=" + pbx.refreshToken;
        body = {};
      }

      dbg("[PBX] Programando refresh…", { model: pbx.model, api });

      const response = await fetch(api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: pbx.model === "S-Series" ? null : JSON.stringify(body),
      });

      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();

      const ok =
        data?.errcode === 0 ||
        data?.status === "Success" ||
        data?.status === "SUCCESS";

      if (!ok) {
        throw new Error(data?.errmsg || "PBX refresh failed");
      }

      const now = Date.now();
      const tokenExpiryTime = now + Number(data.access_token_expire_time) * 1000;
      const refreshTokenExpiryTime = now + Number(data.refresh_token_expire_time) * 1000;

      dispatch(
        setTokens({
          token: data.access_token,
          refreshToken: data.refresh_token || pbx.refreshToken,
          token_expire_time: tokenExpiryTime,
          refreshToken_expire_time: refreshTokenExpiryTime,
        })
      );

      dbg("[PBX] Token refrescado ✔", {
        token_expire_time: tokenExpiryTime,
        expISO: new Date(tokenExpiryTime).toISOString(),
      });
      emit("pbx:tokenRefreshed", {
        token_expire_time: tokenExpiryTime,
        refreshToken_expire_time: refreshTokenExpiryTime,
      });
    } catch (err) {
      console.error("Error al refrescar token PBX:", err);
      emit("pbx:tokenRefreshError", { message: String(err) });

      // Si el refresh token ya venció, necesitarás re-loguear la PBX.
      if (String(err).toUpperCase().includes("TOKEN EXPIRED")) {
        console.warn("[PBX] El refresh token de PBX venció. Reautenticar PBX.");
      }
    }
  };

  useEffect(() => {
    const refreshToken = pbx?.refreshToken;
    const tokenExpiry = pbx?.token_expire_time;
    if (!refreshToken || !tokenExpiry) return;

    const timeLeft = tokenExpiry - Date.now();
    const fireIn = Math.max(timeLeft - TWO_MIN, 0); // T-2m
    dbg("[PBX] Auto-refresh programado", {
      fireInSec: Math.ceil(fireIn / 1000),
      fireAt: new Date(Date.now() + fireIn).toISOString(),
    });

    const id = setTimeout(refreshPBXToken, fireIn);
    return () => clearTimeout(id);
  }, [pbx?.refreshToken, pbx?.token_expire_time]);

  return null;
};

useRefreshPBXToken.propTypes = { refreshInterval: PropTypes.number };
useRefreshPBXToken.defaultProps = { refreshInterval: 15 * 60 * 1000 };

export default useRefreshPBXToken;
